import {
  Box,
  Heading,
  Text,
  Image,
  Flex,
  Badge,
  HStack,
  VStack,
  Icon,
  Card, // Import Card
  Link as ChakraLink, // Import ChakraLink for link styling
  useColorModeValue, // Import useColorModeValue for theme-aware colors
  useToken // Import useToken to get theme values
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { MapPin, ArrowRight } from '@phosphor-icons/react'; // Add ArrowRight

export default function FeaturedSiteCard({ site }) {
  // Generate a slight random rotation for the polaroid effect
  const getRandomRotation = () => {
    // Between -5 and 5 degrees
    const rotation = Math.floor(Math.random() * 10) - 5;
    return `${rotation}deg`;
  };
  
  // Pick a random tape position
  const getTapePosition = () => {
    const positions = ['top-left', 'top-right'];
    return positions[Math.floor(Math.random() * positions.length)];
  };
  
  const rotation = getRandomRotation();
  const tapePosition = getTapePosition();

  // Get theme colors for tape using useToken
  const [tapeColor1, tapeColor2] = useToken('colors', [
    useColorModeValue('accent.gold', 'accent.gold'), // Use accent gold for one tape color
    useColorModeValue('border.default', 'border.default') // Use border default for the other
  ]);

  // Find best available image with fallbacks
  const getPrimaryMedia = () => {
    // Helper to check if URL is valid
    const isValidImageUrl = (url) => url && typeof url === 'string' && url.match(/^https?:\/\/.+/);

    // 1. Try mainImage field first
    if (site.mainImage && isValidImageUrl(site.mainImage)) {
      return { url: site.mainImage, type: 'image' };
    }

    // 2. Try primaryMediaId if specified
    if (site.primaryMediaId) {
      const media = site.media?.find(m => m.id === site.primaryMediaId);
      if (media && isValidImageUrl(media.url)) {
        return media;
      }
    }

    // 3. Try first media item with valid URL
    const validMedia = site.media?.filter(m => m && isValidImageUrl(m.url));
    if (validMedia?.length > 0) {
      return validMedia[0];
    }

    // 4. Fallback to placeholder
    return {
      url: '/textures/parchment.png',
      type: 'image'
    };
  };

  const primaryMedia = getPrimaryMedia();

  return (
    // Wrapper for the card with slight rotation for journal effect
    <Box
      transform={`rotate(${rotation})`}
      mb={6}
      position="relative"
      transition="all 0.3s ease"
      _hover={{
        transform: `rotate(0deg) translateY(-5px)`,
        zIndex: 2
      }}
    >
      {/* Washi tape effect in the top corner */}
      {tapePosition === 'top-left' && (
        <Box
          position="absolute"
          top="-10px"
          left="10px"
          width="40px"
          height="30px"
          bg={`${tapeColor1}66`} // Use theme color with alpha
          transform="rotate(-5deg)"
          borderRadius="2px"
          zIndex={2}
          boxShadow="0 0 3px rgba(0,0,0,0.2)"
        />
      )}

      {tapePosition === 'top-right' && (
        <Box
          position="absolute"
          top="-10px"
          right="10px"
          width="40px"
          height="30px"
          bg={`${tapeColor2}66`} // Use theme color with alpha
          transform="rotate(5deg)"
          borderRadius="2px"
          zIndex={2}
          boxShadow="0 0 3px rgba(0,0,0,0.2)"
        />
      )}

      {/* Polaroid-style photo card */}
      <Card
        as={RouterLink}
        to={`/site/${site.id}`}
        variant="journal" // Apply journal variant
        // bg={useColorModeValue('white', 'gray.50')} // REMOVED: Use theme's bg.surface via variant
        height="100%"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        boxShadow="lg" // Stronger shadow for polaroid effect
        borderWidth="1px"
        // borderColor={useColorModeValue('gray.100', 'gray.200')} // REMOVED: Use theme's border.default via variant
        _hover={{
          textDecoration: 'none',
          boxShadow: 'xl'
        }}
        transition="all 0.2s ease-out"
        pb={6} // Extra padding at bottom for polaroid effect
      >
      <Box
        position="relative"
        height={site.__carouselMode ? '100%' : '200px'}
        width="100%"
        overflow="hidden"
        borderBottom="1px solid"
        borderColor="border.light" // Use theme token
      >
        <Image
          src={primaryMedia.url}
          alt={site.name}
          width="100%"
          height="100%"
          objectFit="cover"
          objectPosition="center"
          fallbackSrc="/textures/parchment.png"
        />
      </Box>

      {/* Show text details only if not in carousel mode */}
      {!site.__carouselMode && (
        <VStack p={4} spacing={3} align="flex-start" flex="1">
          {/* Use base heading style, apply theme color */}
          <Heading size="md" color="text.default" fontFamily="heading" noOfLines={1}>
            {site.name}
          </Heading>

          {site.description && (
            // Use text.muted color, apply caption text style
            <Text noOfLines={2} color="text.muted" fontSize="sm" textStyle="caption">
              {site.description}
            </Text>
          )}

          {site.location?.address && (
            // Use text.muted color
            <HStack spacing={1} color="text.muted"> {/* Reduced spacing */}
              <Icon as={MapPin} boxSize={3} /> {/* Slightly smaller icon */}
              <Text fontSize="xs" noOfLines={1}> {/* Smaller font size */}
                {site.location.country || site.location.address} {/* Prefer country */}
              </Text>
            </HStack>
          )}

          <Flex mt="auto" pt={3} w="full" justify="space-between" align="center">
            {/* Apply artifact badge variant for featured status */}
            <Badge variant="artifact"> {/* Rely on variant definition for color */}
              Featured
            </Badge>
            {/* Style as a link using ChakraLink and journal variant */}
            <ChakraLink as={RouterLink} to={`/site/${site.id}`} variant="journal" fontSize="sm" fontWeight="medium">
              View Details <Icon as={ArrowRight} weight="bold" display="inline-block" ml={1} />
            </ChakraLink>
          </Flex>
        </VStack>
      )}

      {/* If in carousel mode, show site name on top of the image (styles already applied in SiteCarousel) */}
      {/* This section might be redundant if SiteCarousel handles the overlay */}
      {/* Keeping it for now in case FeaturedSiteCard is used outside carousel */}
      {site.__carouselMode && (
        <Box
          position="absolute"
          bottom="0"
          left="0"
          right="0"
          p={4}
          background="linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.4), transparent)"
          color="white" // Keep white for contrast
          // borderBottomRadius="md" // Inherited from Card
        >
          {/* Apply themed heading style */}
          <Heading size="md" mb={1} fontFamily="heading" noOfLines={1}>
            {site.name}
          </Heading>
          {site.location?.address && (
            <HStack spacing={1} opacity={0.9}> {/* Reduced spacing */}
              <Icon as={MapPin} boxSize={3} /> {/* Smaller icon */}
              {/* Apply themed text style */}
              <Text fontSize="xs" fontFamily="body" noOfLines={1}> {/* Smaller font size */}
                 {site.location.country || site.location.address} {/* Prefer country */}
              </Text>
            </HStack>
          )}
        </Box>
      )}
    </Card>
    </Box>
  );
}
